import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, API_BASE } from '../lib/api';
import { FieldEditor, fromApiFields, toApiFields } from '../components/FieldEditor';
import type { EditorField } from '../components/FieldEditor';
import { Banner, Field, Loading, StatusPill } from '../components/ui';

type Recipient = {
  key: string; id?: string; name: string; email: string; phone: string;
  role: 'signer' | 'copy'; routing_order: number;
};

let counter = 0;
const blankRecipient = (order: number): Recipient => ({
  key: `r${counter++}`, name: '', email: '', phone: '', role: 'signer',
  routing_order: order,
});

/**
 * Preparation inside a host application's page.
 *
 * Scoped to one envelope by the embedded session, and deliberately trimmed: no
 * navigation, no other agreements, no tenant settings. Sending goes through the
 * same service as every other channel, so freezing, metering and evidence do
 * not differ because the work happened in a frame.
 */
export function EmbeddedPrepare({ sessionToken }: { sessionToken: string }) {
  const headers = { 'x-embedded-session': sessionToken };
  const [context, setContext] = useState<any>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [fields, setFields] = useState<EditorField[]>([]);
  const [activeAssignee, setActiveAssignee] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<any>(null);
  const attachInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/embed/sender/context', { headers });
      setContext(data);
      setFields(fromApiFields(data.fields));
      setRecipients(
        data.recipients.length
          ? data.recipients.map((r: any, index: number) => ({
              key: `r${counter++}`, id: r.id, name: r.name, email: r.email,
              phone: r.phone || '', role: r.role,
              routing_order: r.routing_order || index + 1,
            }))
          : [blankRecipient(1)],
      );
      const firstSigner = data.recipients.find((r: any) => r.role === 'signer');
      setActiveAssignee(firstSigner?.id ?? null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'This session could not be opened.');
    }
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const saveRecipients = async () => {
    setBusy(true); setError(null);
    try {
      const result = await api.patch('/embed/sender/draft', {
        recipients: recipients.map((r, index) => ({
          // Sending the id keeps this person's identity — and therefore their
          // field assignments — across an address or order change.
          recipient_id: r.id,
          name: r.name, email: r.email, phone: r.phone || undefined,
          role: r.role, routing_order: r.routing_order || index + 1,
        })),
      }, { headers });
      setContext((current: any) => ({ ...current, recipients: result.recipients }));
      setRecipients((current) => current.map((r, index) => ({
        ...r, id: result.recipients[index]?.id ?? r.id,
      })));
      const signer = result.recipients.find((r: any) => r.role === 'signer');
      setActiveAssignee((current) =>
        result.recipients.some((r: any) => r.id === current)
          ? current : signer?.id ?? null);
      setFields((current) => current.map((f) => ({
        ...f,
        recipient_id: result.recipients.some((r: any) => r.id === f.recipient_id)
          ? f.recipient_id : signer?.id ?? null,
      })));
      return result.recipients;
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'Those recipients could not be saved.');
      return null;
    } finally { setBusy(false); }
  };

  const uploadAttachment = async (file: File) => {
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      await api.post('/embed/sender/documents', body, { headers });
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'That attachment could not be added.');
    } finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const saved = await saveRecipients();
      if (!saved) return;
      // Re-point in one step, against the ids that now exist.
      const signer = saved.find((r: any) => r.role === 'signer');
      await api.patch('/embed/sender/draft', {
        fields: toApiFields(fields.map((f) => ({
          ...f,
          recipient_id: saved.some((r: any) => r.id === f.recipient_id)
            ? f.recipient_id : signer?.id ?? null,
        }))),
      }, { headers });
      const result = await api.post('/embed/sender/send', undefined, { headers });
      setSent(result);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'The agreement could not be sent.');
    } finally { setBusy(false); }
  };

  if (error && !context) {
    return (
      <div className="shell shell--narrow section">
        <Banner tone="danger" title="This session cannot be opened">{error}</Banner>
      </div>
    );
  }
  if (!context) {
    return <div className="shell shell--narrow section"><Loading /></div>;
  }

  if (sent) {
    return (
      <div className="shell shell--narrow section stack">
        <div className="card stack center">
          <div style={{ fontSize: '2rem' }} aria-hidden="true">✓</div>
          <h1 style={{ margin: 0, fontSize: '1.2rem' }}>Sent for signature</h1>
          <p className="muted small" style={{ margin: 0 }}>
            {sent.envelope.title}
          </p>
        </div>
        <div className="card stack-s">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Recipients</h2>
          {sent.recipients.map((person: any) => (
            <div className="panel row row--between" key={person.id}>
              <span className="truncate">{person.name}</span>
              <StatusPill status={person.status} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const signers = (context.recipients || []).filter((r: any) => r.role === 'signer');

  return (
    <div className="shell section stack">
      <header className="stack-s">
        <h1 style={{ margin: 0, fontSize: '1.15rem' }}>{context.envelope.title}</h1>
        <p className="card__sub" style={{ margin: 0 }}>
          Prepared inside {context.organization?.name}. Fields and recipients are
          frozen when you send.
        </p>
      </header>

      {error && <Banner tone="danger" title="Please check this">{error}</Banner>}
      {!context.editable && (
        <Banner tone="warn" title="Already sent">
          This agreement has been sent and can no longer be edited.
        </Banner>
      )}

      <div className="card stack">
        <h2 style={{ margin: 0, fontSize: '1rem' }}>Recipients</h2>
        {recipients.map((recipient, index) => (
          <div className="panel stack-s" key={recipient.key}>
            <div className="row row--between">
              <strong style={{ fontSize: '.88rem' }}>
                {recipient.role === 'copy' ? 'Copy recipient' : `Signer ${index + 1}`}
              </strong>
              {recipients.length > 1 && (
                <button type="button" className="btn btn--small btn--ghost"
                        onClick={() => setRecipients(
                          recipients.filter((r) => r.key !== recipient.key))}>
                  Remove
                </button>
              )}
            </div>
            <div className="grid grid--two">
              <Field label="Name" id={`er-n-${recipient.key}`} required>
                <input id={`er-n-${recipient.key}`} className="input"
                       value={recipient.name}
                       onChange={(e) => setRecipients(recipients.map((r) =>
                         r.key === recipient.key ? { ...r, name: e.target.value } : r))} />
              </Field>
              <Field label="Email" id={`er-e-${recipient.key}`} required>
                <input id={`er-e-${recipient.key}`} className="input" type="email"
                       inputMode="email" value={recipient.email}
                       onChange={(e) => setRecipients(recipients.map((r) =>
                         r.key === recipient.key ? { ...r, email: e.target.value } : r))} />
              </Field>
            </div>
            {context.auth_policy === 'sms_otp' && recipient.role === 'signer' && (
              <Field label="Mobile number" id={`er-p-${recipient.key}`} required
                     hint="Required, because this agreement uses SMS passcodes.">
                <input id={`er-p-${recipient.key}`} className="input" type="tel"
                       inputMode="tel" value={recipient.phone}
                       onChange={(e) => setRecipients(recipients.map((r) =>
                         r.key === recipient.key ? { ...r, phone: e.target.value } : r))} />
              </Field>
            )}
          </div>
        ))}
        <div className="row">
          <button type="button" className="btn btn--small" disabled={busy}
                  onClick={() => setRecipients(
                    [...recipients, blankRecipient(recipients.length + 1)])}>
            Add recipient
          </button>
          <button type="button" className="btn btn--small" disabled={busy}
                  onClick={() => void saveRecipients()}>
            {busy ? 'Saving…' : 'Save recipients'}
          </button>
        </div>
      </div>

      <div className="card stack-s">
        <h2 style={{ margin: 0, fontSize: '1rem' }}>Attachments</h2>
        {context.attachments?.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid',
                       gap: 6 }}>
            {context.attachments.map((a: any) => (
              <li key={a.document_id} className="small">
                {a.name} <span className="muted">· {a.page_count}p</span>
              </li>
            ))}
          </ul>
        )}
        <Field label="Add a supporting document" id="embed-attach">
          <input id="embed-attach" ref={attachInput} type="file" className="input"
                 accept="application/pdf,.pdf" style={{ paddingTop: 10 }}
                 disabled={busy || !context.editable}
                 onChange={(event) => {
                   const file = event.target.files?.[0];
                   if (file) void uploadAttachment(file);
                   if (attachInput.current) attachInput.current.value = '';
                 }} />
        </Field>
      </div>

      <FieldEditor
        url={`${API_BASE}/embed/sender/document`}
        headers={headers}
        pageSizes={context.document.page_sizes}
        fields={fields}
        onChange={setFields}
        assignees={signers.map((r: any) => ({ id: r.id, name: r.name }))}
        activeAssignee={activeAssignee}
        onActiveAssignee={setActiveAssignee}
      />

      <div className="actionbar">
        <span className="small muted spacer">
          {fields.length} field{fields.length === 1 ? '' : 's'} placed
        </span>
        <button className="btn btn--primary" disabled={busy || !context.editable
                  || fields.length === 0
                  || recipients.some((r) => !r.name.trim() || !r.email.trim())}
                onClick={() => void send()}>
          {busy ? 'Sending…' : 'Send for signature'}
        </button>
      </div>
    </div>
  );
}
